import {
  Box,
  Button,
  TextField,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback } from "react";
import { FormattedMessage } from "react-intl";

import { GetActiveCampaignContactForTexterQuery } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { CampaignForTexter } from "~frontend/components/threads/ThreadControls/types";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme from "~frontend/theme";
import formatContactName from "~frontend/util/formatContactName";

import { Assignment } from "./types";

const Label = styled(Box)({
  fontSize: "12px",
  fontWeight: 600,
});

export default function AssignmentContactDetails({
  assignment,
  contact,
  campaign,
  handleSaveNote,
  mutationPending,
  unsavedContactNote,
  onChangeUnsavedContactNote,
}: {
  assignment: Assignment;
  contact: GetActiveCampaignContactForTexterQuery["getCampaignContact"];
  campaign: CampaignForTexter;
  handleSaveNote: () => void;
  mutationPending: boolean;
  unsavedContactNote: string | null;
  onChangeUnsavedContactNote: (val: string) => void;
}): ReactElement {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const { subaccountLink } = useSubaccount()!;
  const isNarrow = useMediaQuery(theme.breakpoints.down("wide"));

  const relativeLink = `/threads?thread=${assignment.threadId}&campaign_ids=${campaign.id}&context=${campaign.id}`;

  const link = `${document.location.protocol}//${
    document.location.host
  }${subaccountLink(relativeLink)}`;

  const handleCopyLink = useCallback(async () => {
    await navigator.clipboard.writeText(link);
    snackbar.enqueueSnackbar({
      message: intl.formatMessage({
        defaultMessage: "Copied to clipboard!",
        description: "Message when link was copied to keyboard",
      }),
    });
  }, [link, snackbar, intl]);

  return (
    <FlexBox direction={isNarrow ? "column" : "row"} gap={2}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ marginBottom: "4px" }} variant="h3Bold">
          <FormattedMessage
            defaultMessage="Contact Details"
            description="Header for the panel of contact details in the thread controls"
          />
        </Typography>
        <Label sx={{ fontSize: "12px", fontWeight: 600 }}>
          {formatContactName(
            {
              firstName: assignment.contactFirstName,
              lastName: assignment.contactLastName,
            },
            { formatMessage: intl.formatMessage }
          )}
        </Label>
        <Typography sx={{ marginTop: "24px" }} variant="h3Bold">
          <FormattedMessage
            defaultMessage="Conversation Link"
            description="Header for the sharable conversation link"
          />
        </Typography>
        <Typography variant="body2">
          <Box>
            <FormattedMessage
              defaultMessage="Need to share this conversation with an organizer? Use the button below to copy a link to send to your account admin."
              description="Explanatory message for conversation link"
            />
          </Box>
          <Button
            onClick={handleCopyLink}
            variant="contained"
            color="accessory"
            data-testid="copy-link-button"
            sx={{
              my: 1,
              textWrap: "wrap",
              height: "auto",
            }}
          >
            <FormattedMessage
              defaultMessage="Copy campaign link to clipboard"
              description="Conversation link copy button label"
            />
          </Button>
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <FlexBox>
          <Typography sx={{ flex: 1 }} variant="h3Bold">
            <FormattedMessage
              defaultMessage="Conversation Notes"
              description="Header for the conversation notes input"
            />
          </Typography>

          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSaveNote}
              disabled={mutationPending || unsavedContactNote == null}
            >
              Save
            </Button>
          </Box>
        </FlexBox>
        <TextField
          sx={{ paddingTop: "10px" }}
          multiline
          minRows={3}
          value={unsavedContactNote ?? contact.note?.text ?? ""}
          placeholder={intl.formatMessage({
            defaultMessage:
              "Take notes here to capture details about your conversation with this contact.",
            description: "Placeholder text in the Contact Notes input",
          })}
          onChange={e => onChangeUnsavedContactNote(e.target.value)}
          fullWidth
        />
        <Box>
          <Typography variant="body2">
            {contact.note?.updatedBy && (
              <>
                Updated by {contact.note.updatedBy.fullName} at{" "}
                {DateTime.fromMillis(contact.note.updatedAt).toLocaleString(
                  DateTime.DATETIME_SHORT
                )}
              </>
            )}
          </Typography>
        </Box>
      </Box>
    </FlexBox>
  );
}
